<template>
  <el-dialog
    :visible="true"
    :show-close="false"
    width="580px"
    custom-class="project-form-modal"
    @close="$emit('close')"
  >
    <!-- Header -->
    <div slot="title" class="pf-header">
      <span class="pf-header__title">{{
        isEdit ? "Editar proyecto" : "Nuevo proyecto"
      }}</span>
      <button class="pf-header__close" @click="$emit('close')">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <div class="pf-body">
      <!-- Skeleton while loading -->
      <div v-if="loadingForm" class="pf-skeleton">
        <div class="pf-skeleton__line pf-skeleton__line--label"></div>
        <div class="pf-skeleton__line pf-skeleton__line--input"></div>
        <div
          class="pf-skeleton__line pf-skeleton__line--label"
          style="margin-top: 14px"
        ></div>
        <div class="pf-skeleton__line pf-skeleton__line--input"></div>
        <div style="display: flex; gap: 10px; margin-top: 14px">
          <div style="flex: 1">
            <div class="pf-skeleton__line pf-skeleton__line--label"></div>
            <div class="pf-skeleton__line pf-skeleton__line--input"></div>
          </div>
          <div style="flex: 1">
            <div class="pf-skeleton__line pf-skeleton__line--label"></div>
            <div class="pf-skeleton__line pf-skeleton__line--input"></div>
          </div>
        </div>
      </div>
      <el-form
        v-else
        :model="form"
        :rules="rules"
        ref="form"
        label-position="top"
        size="small"
        @submit.native.prevent="submit"
      >
        <!-- Nombre -->
        <div class="pf-field">
          <div class="pf-field__label">
            Nombre del proyecto <span class="pf-required">*</span>
          </div>
          <el-input v-model="form.name" placeholder="Ej: Rediseño web 2026" />
        </div>

        <!-- Cliente -->
        <div class="pf-field">
          <div class="pf-field__label">Cliente</div>
          <el-select
            v-model="form.client_id"
            placeholder="Busca un cliente..."
            clearable
            filterable
            remote
            :remote-method="searchClients"
            :loading="loadingClients"
            style="width: 100%"
          >
            <el-option
              v-for="c in clients"
              :key="c.id"
              :label="c.name"
              :value="c.id"
            />
          </el-select>
        </div>

        <!-- Presupuesto + Fecha -->
        <div class="pf-row">
          <div class="pf-field" style="flex: 1">
            <div class="pf-field__label">Presupuesto total</div>
            <el-input
              v-model.number="form.budget"
              type="number"
              placeholder="0.00"
            >
              <template slot="prepend">$</template>
            </el-input>
          </div>
          <div class="pf-field" style="flex: 1">
            <div class="pf-field__label">Fecha estimada fin</div>
            <el-date-picker
              v-model="form.end_date"
              type="date"
              placeholder="Opcional"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
              style="width: 100%"
            />
          </div>
        </div>

        <!-- Descripción -->
        <div class="pf-field">
          <div class="pf-field__label">Descripción (opcional)</div>
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="2"
            placeholder="Breve descripción del alcance"
          />
        </div>
      </el-form>
    </div>

    <!-- Footer -->
    <div slot="footer" class="pf-footer">
      <button class="pf-btn pf-btn--cancel" @click="$emit('close')">
        Cancelar
      </button>
      <button class="pf-btn pf-btn--primary" :disabled="saving" @click="submit">
        <span v-if="saving"><i class="el-icon-loading"></i></span>
        <span v-else>{{ isEdit ? "Guardar cambios" : "Crear proyecto" }}</span>
      </button>
    </div>
  </el-dialog>
</template>

<script>
import projectService from "../services/projectService";
import businessService from "../../../../business/services/businessService";

export default {
  name: "ProjectFormModal",
  props: {
    project: { type: Object, default: null }
  },
  data() {
    return {
      form: {
        name: "",
        client_id: null,
        budget: null,
        status: "active",
        end_date: null,
        description: ""
      },
      rules: {
        name: [
          {
            required: true,
            message: "El nombre es requerido",
            trigger: "blur"
          }
        ]
      },
      clients: [],
      loadingClients: false,
      loadingForm: true,
      saving: false
    };
  },
  computed: {
    isEdit() {
      return !!this.project;
    }
  },
  async mounted() {
    this.loadingForm = true;
    await this.loadClients();
    if (this.project) {
      const clientId =
        this.project.client_id ||
        (this.project.client ? this.project.client.id : null);
      if (clientId) await this.ensureClientOption(clientId);
      this.form.name = this.project.name || "";
      this.form.client_id = clientId;
      this.form.budget = this.project.budget || null;
      this.form.status = this.project.status || "active";
      this.form.end_date = this.project.end_date || null;
      this.form.description = this.project.description || "";
    }
    this.loadingForm = false;
  },
  methods: {
    async ensureClientOption(clientId) {
      const existing = this.clients.find(c => c.id === clientId);
      if (existing && existing.name) return; // already present with a valid name
      try {
        const res = await businessService.getBusinessId(clientId);
        const client = res.data.client || res.data;
        if (existing) {
          const idx = this.clients.indexOf(existing);
          this.clients.splice(idx, 1, client);
        } else {
          this.clients.unshift(client);
        }
      } catch (e) {
        /* silent */
      }
    },
    async loadClients(query) {
      this.loadingClients = true;
      try {
        const params = { per_page: 200 };
        if (query) params.name = query;
        const res = await businessService.getBusiness({ params });
        if (res.data && res.data.clients) {
          this.clients = res.data.clients;
        }
      } catch (e) {
        /* silent */
      } finally {
        this.loadingClients = false;
      }
    },
    searchClients(query) {
      if (query && query.length >= 2) {
        this.loadClients(query);
      } else if (!query) {
        this.loadClients();
      }
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        this.saving = true;
        try {
          if (this.isEdit) {
            const res = await projectService.updateProject(
              this.project.id,
              this.form
            );
            if (res.data.success) {
              this.$notifications.success({
                message: "Proyecto actualizado correctamente."
              });
              this.$emit("updated", res.data.data);
            }
          } else {
            const res = await projectService.createProject(this.form);
            if (res.data.success) {
              this.$notifications.success({
                message: "Proyecto creado correctamente."
              });
              this.$emit("created", {
                ...this.form,
                id: res.data.data.id,
                invoice_count: 0
              });
            }
          }
        } finally {
          this.saving = false;
        }
      });
    }
  }
};
</script>

<style lang="scss">
.project-form-modal {
  border-radius: 12px !important;
  overflow: hidden;

  .el-dialog__header {
    padding: 0;
    border-bottom: 1px solid #eef0f8;
  }

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__footer {
    padding: 0;
    border-top: 1px solid #eef0f8;
  }

  .el-input-group__prepend {
    padding: 0 12px;
  }

  .el-input-group--prepend .el-input__inner {
    padding-left: 12px;
  }

  @media (max-width: 768px) {
    width: 100% !important;
    margin: 0 !important;
    border-radius: 0 !important;
    max-height: 100vh !important;

    .el-dialog__body {
      overflow-y: auto;
      max-height: calc(100vh - 108px);
    }
  }
}
</style>

<style lang="scss" scoped>
/* ── Header ── */
.pf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #2d2b55;
  }

  &__close {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    color: #c0c4cc;
    padding: 2px;
    border-radius: 4px;
    transition: all 0.15s;

    &:hover {
      color: #2d2b55;
      background: #f5f5f5;
    }
  }
}

/* ── Body ── */
.pf-body {
  padding: 20px;
}

/* ── Fields ── */
.pf-field {
  margin-bottom: 14px;

  &__label {
    font-size: 11px;
    color: #9ca3af;
    margin-bottom: 5px;
    font-weight: 500;
  }
}

.pf-required {
  color: #f56c6c;
}

/* ── Skeleton ── */
.pf-skeleton {
  padding: 0;

  &__line {
    border-radius: 6px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: pf-shimmer 1.2s ease-in-out infinite;

    &--label {
      width: 100px;
      height: 12px;
      margin-bottom: 8px;
    }

    &--input {
      width: 100%;
      height: 32px;
    }
  }
}

@keyframes pf-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.pf-row {
  display: flex;
  gap: 10px;
}

/* ── Footer ── */
.pf-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
}

.pf-btn {
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: none;

  &--cancel {
    background: transparent;
    border: 1px solid #e4eaf6;
    color: #606266;

    &:hover {
      background: #f5f5f5;
    }
  }

  &--primary {
    background: #3fdbbf;
    color: #fff;
    font-weight: 600;

    &:hover {
      background: #0e8c87;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
